import { Box, Icon, Paragraph, Tooltip } from '@zillow/constellation-10';
import { IconCheckmarkCircleFilled, IconCloseCircleFilled, IconMinusCircleFilled } from '@zillow/constellation-icons';
import { SatelliteRenterProfileScreeningCriteriaFilterType } from '@zg-rentals/ts-satellite-client';
import type { SatelliteWebLandlordLeadManagementTableRowScreeningCriterionDto } from '@zg-rentals/ts-satellite-client';

type ScreeningCriteriaMatchesProps = {
  criteria: Array<SatelliteWebLandlordLeadManagementTableRowScreeningCriterionDto>;
  isRenterProfileStyle?: boolean;
};

const CRITERIA_TYPES_PRIORITY = [
  {
    type: SatelliteRenterProfileScreeningCriteriaFilterType.MIN_MONTHLY_INCOME,
    displayName: 'Monthly income',
  },
  {
    type: SatelliteRenterProfileScreeningCriteriaFilterType.MIN_CREDIT_SCORE,
    displayName: 'Credit score',
  },
  {
    type: SatelliteRenterProfileScreeningCriteriaFilterType.PET_POLICY,
    displayName: 'Pets',
  },
];

function getMatchSortOrder(hasMatchingCriterion?: boolean | null, isNegotiable?: boolean | null): number {
  if (hasMatchingCriterion && !isNegotiable) return 0;
  if (!hasMatchingCriterion && !isNegotiable) return 1;
  return 2;
}

export default function ScreeningCriteriaMatches({
  criteria,
  isRenterProfileStyle = false,
}: ScreeningCriteriaMatchesProps) {
  const criteriaMatched = criteria;

  const sortedCriteria = CRITERIA_TYPES_PRIORITY.map((criteriaType) => {
    const criterion = criteriaMatched.find((c) => c.screeningCriteriaType === criteriaType.type);
    if (!criterion) return null;
    return { ...criteriaType, criterion };
  })
    .filter((item): item is NonNullable<typeof item> => item !== null)
    .sort(
      (a, b) =>
        getMatchSortOrder(a.criterion.hasMatchingCriterion, a.criterion.isNegotiable) -
        getMatchSortOrder(b.criterion.hasMatchingCriterion, b.criterion.isNegotiable),
    );

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tight' }}>
      {sortedCriteria.map(({ type, displayName, criterion }) => {
        const {
          hasMatchingCriterion,
          isNegotiable,
          isCriterionSet,
          rentersCriterionSubmittedDisplay,
          renterDataToolTipText,
          screeningCriterionDisplay,
          screeningCriteriaType,
        } = criterion;

        const hasMatchedCriteriaItem = hasMatchingCriterion && isCriterionSet;
        const hasNotMatchedCriteriaItem = !hasMatchingCriterion && isCriterionSet && !isNegotiable;
        const mayMatchCriteriaItem = !hasMatchingCriterion && isCriterionSet && isNegotiable;
        const shouldDisplayRenterDataTooltip =
          isRenterProfileStyle &&
          !!renterDataToolTipText &&
          type === SatelliteRenterProfileScreeningCriteriaFilterType.MIN_MONTHLY_INCOME;

        return (
          <Box
            key={screeningCriteriaType}
            css={{
              display: 'flex',
              gap: 'tighter',
              alignItems: 'flex-start',
            }}
          >
            {hasMatchedCriteriaItem && (
              <Icon title="Matches criteria" size="md" css={{ color: 'icon.feedback.success.hero', flexShrink: 0 }}>
                <IconCheckmarkCircleFilled />
              </Icon>
            )}
            {hasNotMatchedCriteriaItem && (
              <Icon
                title="Does not match criteria"
                size="md"
                css={{ color: 'icon.feedback.critical.hero', flexShrink: 0 }}
              >
                <IconCloseCircleFilled />
              </Icon>
            )}
            {mayMatchCriteriaItem && (
              <Icon title="May match criteria" size="md" css={{ color: 'icon.feedback.warning.strong', flexShrink: 0 }}>
                <IconMinusCircleFilled />
              </Icon>
            )}

            <Box css={{ display: 'flex', flexDirection: 'column', gap: 'tightest' }}>
              <Box css={{ display: 'flex', alignItems: 'center' }}>
                <Paragraph textStyle={isRenterProfileStyle ? 'body-sm' : 'body-sm-bold'}>
                  {displayName}:&nbsp;
                </Paragraph>
                {rentersCriterionSubmittedDisplay ? (
                  <Box css={{ display: 'flex', alignItems: 'center' }}>
                    <Paragraph
                      textStyle="body-sm"
                      css={{
                        color: isRenterProfileStyle ? 'text.subtle' : undefined,
                      }}
                    >
                      {rentersCriterionSubmittedDisplay}
                    </Paragraph>
                    {shouldDisplayRenterDataTooltip && (
                      <Tooltip content={renterDataToolTipText} placement="bottom" shouldAwaitInteractionResponse />
                    )}
                  </Box>
                ) : (
                  <Paragraph textStyle="body-sm" css={{ color: 'text.subtle' }}>
                    Not provided
                  </Paragraph>
                )}
              </Box>
              {screeningCriterionDisplay && (
                <Paragraph textStyle="body-xs" css={{ color: 'text.subtle' }}>
                  Criteria: {screeningCriterionDisplay}
                </Paragraph>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
